import React from 'react';
import hoistNonReactStatics from 'hoist-non-react-statics';
import _debounce from 'lodash/debounce';

import { useLocation } from 'react-router-dom';

import { SearchField } from './SearchField';
import { useQueryParams } from '../../providers/QueryParamsProvider';

export const withQueryParamSubscriber = <T extends InjectedProps>(
  Component: React.ComponentType<T>
) => {
  const Wrapper = (props: Omit<T, keyof InjectedProps>) => {
    const { pathname } = useLocation();
    const { query, replace } = useQueryParams();

    const updateSearchParam = React.useMemo(
      () =>
        _debounce((value: string) => {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { search, pageRequested, searchAfter, page, ...rest } = query;

          if (value) {
            replace(pathname, { query: { ...rest, search: value } });
          } else {
            replace(pathname, { query: { ...rest } });
          }
        }, 500),
      [query, replace, pathname]
    );

    const handleChange: ChangeHandler = (event) => {
      const { value } = event.target;

      updateSearchParam(value);
    };

    const initialValue = query.search ?? undefined;

    const newProps = {
      ...props,
      onChange: handleChange,
      defaultValue: initialValue,
    } as unknown as T;

    return <Component {...newProps} />;
  };

  const displayName = Component.displayName ?? Component.name ?? 'Component';
  Wrapper.displayName = `withQueryParamSubscriber(${displayName})`;
  return hoistNonReactStatics(Wrapper, Component);
};

export const SearchFieldWithQueryParamController =
  withQueryParamSubscriber(SearchField);

type ChangeHandler = React.ChangeEventHandler<HTMLInputElement>;

type InjectedProps = {
  onChange: ChangeHandler;
  defaultValue: string | undefined;
};
